import { NavLink } from 'react-router-dom';
import {
  Home,
  Cloud,
  Mail,
  Shield,
  Activity,
  FileText,
  Users,
  ShieldCheck,
  MessageSquare,
  FolderTree,
  UserCheck,
  Globe,
  Zap,
  Wrench,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { hasPermission } from '../lib/api';

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  external?: string;
  /** Welche Permission nötig ist um diesen Eintrag zu sehen. Ohne = immer sichtbar. */
  permission?: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', icon: Home, label: 'Home', permission: 'dashboard.home' },
  { to: '/cloud', icon: Cloud, label: 'Cloud', external: 'https://cloud.rla-solutions.de', permission: 'cloud.view' },
  { to: '/mail', icon: Mail, label: 'Mail', external: 'https://email.rla-solutions.de', permission: 'mail.view' },
  { to: '/admin', icon: Shield, label: 'Admin', permission: 'admin.view' },
  { to: '/users', icon: Users, label: 'Benutzer', permission: 'admin.users.view' },
  { to: '/roles', icon: ShieldCheck, label: 'Rollen', permission: 'admin.roles.manage' },
  { to: '/chat', icon: MessageSquare, label: 'Chat', permission: 'chat.view' },
  { to: '/accounts', icon: UserCheck, label: 'Accounts', permission: 'accounts.view' },
  { to: '/groups', icon: FolderTree, label: 'Gruppen', permission: 'groups.view' },
  { to: '/ausweis', icon: Zap, label: 'Energieausweis', permission: 'admin.view' },
  { to: '/tools', icon: Wrench, label: 'Tools', permission: 'admin.view' },
  { to: '/website', icon: Globe, label: 'Website', permission: 'dashboard.monitoring' },
  { to: '/monitoring', icon: Activity, label: 'Monitoring', permission: 'dashboard.monitoring' },
  { to: '/impressum', icon: FileText, label: 'Mehr', permission: 'dashboard.impressum' },
];

export function Sidebar() {
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const permissions = me?.permissions;

  // Nur Items anzeigen für die der User die Berechtigung hat
  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.permission || hasPermission(permissions, item.permission),
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setOpen(!open)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-white shadow-md border border-slate-200"
        aria-label="Navigation"
      >
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Overlay */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-rla-900 text-white flex flex-col
          transform transition-transform duration-200 ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo / Header */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
          <div className="w-8 h-8 rounded-lg bg-rla-500 flex items-center justify-center font-bold text-sm">
            RLA
          </div>
          <div>
            <div className="font-semibold text-sm">RLA Dashboard</div>
            <div className="text-xs text-white/50">{me?.roleName ?? 'Admin-Portal'}</div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleItems.map(({ to, icon: Icon, label, external }) =>
            external ? (
              <a
                key={to}
                href={external}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/70 hover:bg-white/10 hover:text-white transition"
                onClick={() => setOpen(false)}
              >
                <Icon size={18} />
                <span>{label}</span>
                <span className="ml-auto text-[10px] bg-white/10 px-1.5 py-0.5 rounded">extern</span>
              </a>
            ) : (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition ${
                    isActive
                      ? 'bg-rla-500 text-white font-medium'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                <Icon size={18} />
                <span>{label}</span>
              </NavLink>
            ),
          )}
        </nav>

        {/* Footer / User */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="text-xs text-white/50 mb-2 truncate">{me?.email ?? ''}</div>
          <button
            onClick={() => { void logout(); }}
            className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition w-full"
          >
            <LogOut size={16} />
            <span>Abmelden</span>
          </button>
        </div>
      </aside>
    </>
  );
}
